import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';
import authService from '../services/authService';
import QRCode from 'qrcode';

const homePathForRole = (role) => {
  if (role === 'superadmin') return '/admin/companies';
  if (role === 'companyadmin') return '/admin/templates';
  return '/portal/templates';
};

export default function MandatoryTwoFactorSetupPage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const productName = branding.productName || 'inkedform';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [verificationCode, setVerificationCode] = useState('');

  useEffect(() => {
    if (!user?.requiresTwoFactorSetup) {
      // User doesn't need 2FA setup, redirect based on role
      navigate(homePathForRole(user?.role), { replace: true });
      return;
    }

    handleBeginSetup();
  }, [user, navigate]);

  const handleBeginSetup = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await authService.beginTwoFactorSetup();
      
      setManualKey(response.manualEntryKey);
      
      const qrUrl = await QRCode.toDataURL(response.qrCodeUri);
      setQrCodeUrl(qrUrl);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to start 2FA setup');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      setError('Please enter a 6-digit code');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await authService.confirmTwoFactorSetup(verificationCode);
      
      // Redirect based on user role (companyadmin → admin workspace)
      navigate(homePathForRole(user?.role), { replace: true });
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code');
      setVerificationCode('');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="login-container">
      <div className="login-box" style={{ maxWidth: '500px' }}>
        <div className="login-header">
          <h1>{productName}</h1>
          <h2>Security Setup Required</h2>
        </div>

        {error && (
          <div className="alert alert-error" style={{ margin: '0 1.5rem 1rem' }}>
            {error}
          </div>
        )}

        <div className="login-form">
          <div className="alert" style={{ background: '#f6f8fa', color: '#333', marginBottom: '1rem' }}>
            Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
          </div>

          <div style={{
            textAlign: 'center',
            marginBottom: '1.5rem',
            padding: '1rem',
            background: '#fff',
            borderRadius: '8px',
            border: '1px solid #e1e4e8'
          }}>
            {qrCodeUrl && <img src={qrCodeUrl} alt="QR Code" style={{ maxWidth: '200px' }} />}
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontSize: '0.9rem',
              fontWeight: '600',
              color: '#333'
            }}>
              Manual Entry Key:
            </label>
            <code style={{
              display: 'block',
              padding: '0.75rem',
              background: '#f6f8fa',
              border: '1px solid #e1e4e8',
              borderRadius: '4px',
              fontSize: '0.85rem',
              wordBreak: 'break-all',
              color: '#333'
            }}>
              {manualKey}
            </code>
          </div>

          <form onSubmit={handleVerify}>
            <div className="form-group">
              <label htmlFor="verificationCode">Enter 6-Digit Code</label>
              <input
                type="text"
                id="verificationCode"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                inputMode="numeric"
                required
                autoFocus
                disabled={loading}
                style={{
                  textAlign: 'center',
                  fontSize: '1.5rem',
                  letterSpacing: '0.5rem',
                  fontFamily: 'monospace'
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              className="btn btn-primary btn-block"
              style={{ marginBottom: '0.75rem' }}
            >
              {loading ? 'Verifying...' : 'Verify & Complete Setup'}
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="btn btn-block"
              style={{
                backgroundColor: 'transparent',
                color: '#666',
                border: '1px solid #ddd'
              }}
            >
              Logout
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
